<script setup lang="ts">
import { useDebounce } from '@vueuse/core'
import { LucideSearch, LucideNewspaper, LucideCircleX } from 'lucide-vue-next'
import { FeaturePagination } from '~/components/feature/pagination'
import type { SearchResponse } from 'meilisearch'
import {
  EntityExchangeOfficeItem,
  EntityExchangeOfficeItemSkeleton
} from '~/components/entity/exchange-office'
import type { IExchangeOffice, IExchangeOfficeQueryParams } from '~/components/entity/exchange-office'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '~/components/ui/select'
import type { ILocation, IResponseData } from '~/types'

const config = useRuntimeConfig()
const route = useRoute()
const router = useRouter()
const { locale } = useI18n()

const hitsPerPage = Number(route.query.per_page || 10)
const searchQueryString = ref(String(route.query.q || ''))
const currentPage = ref(Number(route.query.page) || 1)

const debouncedQuery = useDebounce(searchQueryString, 500)

// Location filter: the head branch city or region of an exchange office ("l" in the URL)
const ALL_LOCATIONS = 'all'
const selectedLocation = ref(/^\d+$/.test(String(route.query.l)) ? String(route.query.l) : ALL_LOCATIONS)

const { data: locationsData } = useApi<IResponseData<ILocation[]>>(
  () => useUrlWithParams('/locations', { per_page: 1000 }),
  { key: 'exchange-offices-locations' }
)
const locations = computed(() => locationsData.value?.data || [])

// Compute search key based on all filter dependencies
const searchKey = computed(() =>
  ['exchange-offices-search', debouncedQuery.value, selectedLocation.value, currentPage.value].join('-')
)

// Use useAsyncData for SSR support
const {
  data: result,
  pending: isSearching,
  error: searchError
} = await useAsyncData<SearchResponse<IExchangeOffice> | null>(
  searchKey,
  async () => {
    const { search } = useMeiliIndexSearch(`${config.public.meiliEnvironment}_exchange_offices`)

    try {
      return (await search(debouncedQuery.value, {
        hitsPerPage,
        sort: ['weight:desc', 'highlight:desc', 'updated_at:desc'],
        page: currentPage.value,
        filter:
          selectedLocation.value === ALL_LOCATIONS
            ? undefined
            : [`location_ids = ${Number(selectedLocation.value)}`],
        attributesToHighlight: ['title'],
        attributesToSearchOn: [
          'title.ky',
          'title.ru',
          'description.ky',
          'description.ru',
          'license',
          'okpo'
        ]
      })) as unknown as SearchResponse<IExchangeOffice>
    } catch (e) {
      console.error('MeiliSearch fetch error:', e)
      return null
    }
  },
  {
    server: true,
    lazy: true,
    watch: false,
    default: () => null
  }
)

const hasError = computed(() => !!searchError.value)

const typedHits = computed(() => {
  return (result.value?.hits || []).map((hit) => ({
    id: hit.id,
    title: hit._formatted?.title[locale.value] || hit.title[locale.value],
    logo: hit.logo,
    head_branch_location: hit.head_branch_location,
    license: hit.license,
    okpo: hit.okpo,
    mobile_phone: hit.mobile_phone,
    highlight: hit.highlight
  }))
})

// Initialize component
const isInitializing = ref(true)

onMounted(() => {
  isInitializing.value = false
})

const props = withDefaults(
  defineProps<{
    queryParams?: IExchangeOfficeQueryParams
    hideSearch?: boolean
  }>(),
  {
    queryParams: () => ({
      per_page: '10',
      q: ''
    }),
    hideSearch: false
  }
)

watch(
  () => props.queryParams,
  () => {
    if (isInitializing.value) return
    currentPage.value = 1
    searchQueryString.value = props.queryParams?.q || ''
  },
  { deep: true }
)

watch(debouncedQuery, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      q: debouncedQuery.value,
      page: undefined
    }
  })
})

watch(selectedLocation, () => {
  if (isInitializing.value) return
  currentPage.value = 1
  router.replace({
    query: {
      ...route.query,
      l: selectedLocation.value === ALL_LOCATIONS ? undefined : selectedLocation.value,
      page: undefined
    }
  })
})

const pageLink = (page: number) => {
  const params = new URLSearchParams({ q: searchQueryString.value, page: String(page) })
  if (selectedLocation.value !== ALL_LOCATIONS) params.set('l', selectedLocation.value)
  return `?${params.toString()}`
}

const changePage = (page: number) => {
  router.replace({
    query: {
      ...route.query,
      q: searchQueryString.value,
      page
    }
  })
  currentPage.value = page
}
</script>

<template>
  <div class="space-y-5">
    <div v-if="!hideSearch" class="flex flex-col gap-3 md:flex-row">
      <div class="relative flex-1">
        <Input
          v-model="searchQueryString"
          :placeholder="$t('common.search_placeholder')"
          class="h-[52px] pl-14 pr-14"
        />
        <LucideSearch class="absolute left-4 top-3.5 size-6 text-gray-600" />
        <Button
          v-if="searchQueryString"
          size="icon"
          variant="link"
          class="absolute right-4 top-3.5 size-6 text-red-400 hover:text-red-400"
          @click="searchQueryString = ''"
        >
          <LucideCircleX />
        </Button>
      </div>
      <Select v-model="selectedLocation">
        <SelectTrigger
          :aria-label="$t('widget.exchange_office.meili_search.select_location')"
          variant="searchForm"
          class="flex-shrink-0 md:w-[252px]"
        >
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem :value="ALL_LOCATIONS">
            {{ $t('widget.exchange_office.meili_search.all_locations') }}
          </SelectItem>
          <SelectItem
            v-for="location in locations"
            :key="location.id"
            :value="String(location.id)"
          >
            {{ location.title }}
          </SelectItem>
        </SelectContent>
      </Select>
    </div>

    <section v-if="!isSearching && !hasError && result" aria-labelledby="exchange-offices">
      <div v-if="result.hits?.length" class="CardsList">
        <Heading id="exchange-offices" tag="h2" class="sr-only">{{
          $t('common.exchange_offices')
        }}</Heading>
        <template v-for="item in typedHits" :key="item.id">
          <EntityExchangeOfficeItem :item="item" :hide-organization-details="true" />
        </template>

        <div class="mt-7 flex justify-end">
          <FeaturePagination
            :meta="{
              current_page: result.page || 1,
              total: result.totalHits || 0,
              per_page: result.hitsPerPage || 0,
              last_page: result.totalPages || 0
            }"
            :links="{
              prev: currentPage > 1 ? pageLink(result.page ? result.page - 1 : 1) : null,
              next:
                currentPage < (result.totalPages || 1)
                  ? pageLink(result.page ? result.page + 1 : 1)
                  : null
            }"
            @change="changePage"
          />
        </div>
      </div>

      <Alert v-else variant="blue">
        <LucideNewspaper />
        <AlertDescription>{{ $t('common.nothing_found') }}</AlertDescription>
      </Alert>
    </section>

    <section v-else-if="isSearching">
      <EntityExchangeOfficeItemSkeleton :count="hitsPerPage" :hide-organization-details="false" />
    </section>

    <section v-else-if="hasError">
      <Alert variant="yellow">
        <AlertTitle>{{ $t('common.error_loading_title') }}</AlertTitle>
        <AlertDescription>{{ $t('common.error_loading_description') }}</AlertDescription>
      </Alert>
    </section>
  </div>
</template>
